import { ColumnState, FilterModel } from 'ag-grid-enterprise';
import { isEqual } from 'lodash';
import { action, computed, observable, runInAction, set, toJS } from 'mobx';
import {
    autostore,
    bind,
    HttpClient,
    IAccessTokenStore,
    IDisposable,
    IMultiSelectOption,
    ITimezonePreferenceStore,
    mobxEqual,
} from 'Mri.Web.Common';
import { Log, User, UserManager, UserManagerSettings } from 'oidc-client';
import { DisplayViewTypes } from '../DisplayFilters/DisplayViewSelector';

export interface IUserColumnPreset {
    type?: string;
    name: string;
    columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
    configColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
}

export interface IUserGridColumnPreset {
    name: string;
    columns: ColumnState[];
}
export interface IUserGridFilterPreset {
    name: string;
    filterModel: FilterModel;
    textSearch?: string | null;
}

export class AccountStore implements IAccessTokenStore, ITimezonePreferenceStore {
    @computed
    public get user() {
        return this.userStorage.user;
    }

    @computed
    public get isLoggedIn() {
        return this.user != null;
    }

    @computed
    public get timezone() {
        return this.user?.preferredTimezone;
    }

    public get portalUrl() {
        return this.authority;
    }

    public get internalUrl() {
        if(this.authority.includes("localhost")) {
            return "https://localhost:5002";
        }
        return "https://internal.mri-inc.net";
    }
    
    public constructor(httpClient: HttpClient, oidcConfig: UserManagerSettings) {
        this.httpClient = httpClient;

        this.authority = oidcConfig.authority ?? '';
        Log.logger = console;
        Log.level = Log.WARN;
        this.userManager = new UserManager(oidcConfig);
        this.userManager.events.addUserSignedOut(
            action(() => {
                this.userManager.removeUser();
                this.userStorage.user = undefined;
            })
        );

        autostore(this.userStorage, 'AccountStore.UserStorage', {
            useLocalStorage: false,
            useSessionStorage: true,
            useCustomStorage: false,
        });

        autostore(this.tabStorage, 'AccountStore.TabStorage', {
            useLocalStorage: false,
            useSessionStorage: true,
            useCustomStorage: false,
        });

        this.configureAutoStore(false);
    }

    public signinRedirect(state: any) {
        this.userManager.signinRedirect({
            state,
        });
    }

    @action
    public async signinRedirectCallback(): Promise<any> {
        try {
            const user = await this.userManager.signinRedirectCallback();
            if (user) {
                // Get the ApplicationUser from the database
                this.userStorage.user = await this.httpClient.get<Mri.Web.Internal.Controllers.Account.UserModel>(
                    `${this.authority}/api/v1/account`
                );
                this.configureAutoStore();
                return user.state;
            }
        } catch (e: any) {
            // This is a non-critical error, so catch and log only
            console.error(e);
        }

        return undefined;
    }

    public async renewCallback() {
        await this.userManager.signinSilentCallback();
    }

    public async signoutRedirect() {
        return this.userManager.signoutRedirect();
    }

    @action
    public async loadUserAsync() {
        try {
            const user = await this.userManager.getUser();
            if (user) {
                // Get the ApplicationUser from the database,
                const promise = this.tryLoadUserAsync();

                // However only await if not cached
                if (!this.userStorage.user) {
                    await promise;
                }

                this.configureAutoStore();
            }
        } catch (e: any) {
            // This is a non-critical error, so catch and log only
            console.error(e);
        }
    }

    public setMetaData(metaData: Mri.Web.Internal.Controllers.Account.MetaDataModel) {
        return this.changeSettingsAsync({
            preferredTheme: this.user!.preferredTheme,
            fahrenheitPreferred: this.user!.fahrenheitPreferred,
            multiSortPreferred: this.user!.multiSortPreferred,
            subscribedToAlerts: this.user!.subscribedToAlerts,
            viewedChangelog: this.user!.viewedChangelog,
            metaData,
            statusVuNewTab: this.user!.statusVuNewTab,
            preferredTimezone: this.user!.preferredTimezone,
        });
    }

    public setViewedChangelog(viewedChangelog: string) {
        return this.changeSettingsAsync({
            preferredTheme: this.user!.preferredTheme,
            fahrenheitPreferred: this.user!.fahrenheitPreferred,
            multiSortPreferred: this.user!.multiSortPreferred,
            subscribedToAlerts: this.user!.subscribedToAlerts,
            viewedChangelog,
            metaData: this.user!.metaData,
            statusVuNewTab: this.user!.statusVuNewTab,
            preferredTimezone: this.user!.preferredTimezone,
        });
    }

    @action
    public async changeSettingsAsync(model: Mri.Web.Internal.Controllers.Account.ChangeUserSettingsModel) {
        const user = await this.httpClient.post<Mri.Web.Internal.Controllers.Account.UserModel>(
            `${this.authority}/api/v1/manage/settings`,
            model
        );
        this.userStorage.user = user;
    }
    
    @bind
    @action
    public mergeOldAccountStorageValues(){
        let tempStorage = this.storage as any;
        if(tempStorage.activeGridColumns) {
            this.storage.noActivePresetGridColumns = tempStorage.activeGridColumns;
            delete (this.storage as any).activeGridColumns;
        }
        if(tempStorage.usePinnedColumnContainerMaxWidth) {
            this.storage.gridLeftPinnedContainerScrollable = tempStorage.usePinnedColumnContainerMaxWidth;
            delete (this.storage as any).usePinnedColumnContainerMaxWidth;
        }
        if(tempStorage.gridPinnedColumnContainerWidth) {
            this.storage.gridLeftPinnedContainerWidth = tempStorage.gridPinnedColumnContainerWidth;
            delete (this.storage as any).gridPinnedColumnContainerWidth;
        }
    }

    public async getAccessToken(): Promise<string | undefined> {
        let user = await this.userManager.getUser();
        if (user) {
            if (user.expired) {
                this.clearRogueOidcTokensFromLocalStorage();
                user = await this.signinSilentSafe();
            }

            return user.access_token;
        } else {
            await this.signinSilentSafe();
            return this.getAccessToken();
        }
    }

    public isInRole(role: string) {
        const userRoles = this.user?.roles ?? [];
        return userRoles.includes(role);
    }

    public isInRoles(roles: string[]) {
        const userRoles = this.user?.roles ?? [];
        return roles.filter(r => userRoles.includes(r)).length > 0;
    }

    @computed
    public get isTestUser() {
        return this.user?.userName == 'test';
    }

    @computed
    public get isSuperAdmin() {
        return this.user?.email == 'bbearchell@boldvu.com';
    }

    private signinSilentSafe() {
        if (!this.signinSilentPromise) {
            this.signinSilentPromise = this.signinSilentRetry();
        }

        return this.signinSilentPromise;
    }

    // Clears local storage of non-user oidc tokens
    // This is to prevent a build up of rogue tokens that cause the app to fail to load
    private clearRogueOidcTokensFromLocalStorage = () => {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('oidc') && !key.startsWith('oidc.user')) {
                localStorage.removeItem(key);
            }
        });
    };

    private async signinSilentRetry() {
        let retryStep = 0;
        do {
            try {
                return await this.userManager.signinSilent();
            } catch (err) {
                console.log('Error caught while trying to sign in safely', err);
            }
        } while (retryStep++ < 4);

        throw new Error('Failed trying to signinSilent');
    }

    @action
    private configureAutoStore(useCustomStorage: boolean = true) {
        if (this.autoStoreDisposable) this.autoStoreDisposable.dispose();

        this.autoStoreDisposable = autostore(this.storage, 'AccountStore.AccountStorage', {
            useLocalStorage: true,
            useSessionStorage: false,
            useCustomStorage,
            selector: () => (this.user?.metaData.internalAccountStorage as AccountStorage) ?? undefined,
            onChange: storage => {
                if (this.user) {
                    const other = Object.assign(
                        Object.create(Object.getPrototypeOf(storage)),
                        toJS(this.user.metaData.internalAccountStorage)
                    );

                    if (!mobxEqual(storage, other)) {
                        const diffs = this.getStorageDifferences(this.user.metaData.internalAccountStorage, storage);

                        if (diffs.length > 0) {
                            runInAction(() => {
                                // Update both the user metaData and the observable storage
                                this.user!.metaData.internalAccountStorage = storage;
                                this.storage = storage; // <-- This ensures MobX reactivity
                                if(!this.suppressStorageSync) this.setMetaData(this.user!.metaData);
                            });
                        }
                    }
                }
            },
        });
    }

    @action
    private async tryLoadUserAsync() {
        try {
            this.userStorage.user = await this.httpClient.get<Mri.Web.Internal.Controllers.Account.UserModel>(
                `${this.authority}/api/v1/account`
            );
        } catch (e: any) {
            console.error(e);
        }
    }

    /**
     * Compares two AccountStorage objects to see if they are equal
     * Returns false if any of the properties are different
     * @param storage1
     * @param storage2
     * @private
     */
    @action
    private getStorageDifferences(storage1: AccountStorage, storage2: AccountStorage): string[] {
        const differences: string[] = [];

        const comparisons: [keyof AccountStorage, string][] = [
            ['activeColumnPreset', 'activeColumnPreset'],
            ['activeStickyPreset', 'activeStickyPreset'],
            ['displayAggregateColumns', 'displayAggregateColumns'],
            ['displayTableColumns', 'displayTableColumns'],
            ['displayTableConfigColumns', 'displayTableConfigColumns'],
            ['displayTableStickyColumns', 'displayTableStickyColumns'],
            ['activeGridColumnPreset', 'activeGridColumnPreset'],
            ['gridColumnPresets', 'gridColumnPresets'],
            ['gridTheme', 'gridTheme'],
            ['gridThemeSize', 'gridThemeSize'],
            ['gridRowHeight', 'gridRowHeight'],
            ['gridPopVuRowHeight', 'gridPopVuRowHeight'],
            ['gridGroupHeaderHeight', 'gridGroupHeaderHeight'],
            ['gridColumnWidth', 'gridColumnWidth'],
            ['gridWrapText', 'gridWrapText'],
            ['gridBoldText', 'gridBoldText'],
            ['gridDecimals', 'gridDecimals'],
            ['gridCenterAlignHeaders', 'gridCenterAlignHeaders'],
            ['gridCenterAlignColumns', 'gridCenterAlignColumns'],
            ['gridLeftPinnedContainerScrollable', 'gridLeftPinnedContainerScrollable'],
            ['gridLeftPinnedContainerWidth', 'gridLeftPinnedContainerWidth'],
            ['gridRightPinnedContainerScrollable', 'gridRightPinnedContainerScrollable'],
            ['gridRightPinnedContainerWidth', 'gridRightPinnedContainerWidth'],
            ['gridAutoUpdatePresets', 'gridAutoUpdatePresets'],
            ['hideUnits', 'hideUnits'],
            ['mapZoomLevel', 'mapZoomLevel'],
            ['mapShowDisplayList', 'mapShowDisplayList'],
            ['presetColumns', 'presetColumns'],
            ['presetStickyColumns', 'presetStickyColumns'],
            ['version', 'version'],
            ['viewType', 'viewType'],
            ['wrappedHeaders', 'wrappedHeaders'],
            ['displaysPreference', 'displaysPreference'],
            ['noActivePresetGridColumns', 'noActivePresetGridColumns'],
        ];

        for (const [key, name] of comparisons) {
            if (!isEqual(storage1?.[key], storage2?.[key])) {
                differences.push(name);
            }
        }

        return differences;
    }
    
    @observable
    public storage = new AccountStorage();
    @observable
    public userStorage = new UserStorage();
    @observable
    public tabStorage = new TabStorage();
    @observable
    public suppressStorageSync = false;
    
    private autoStoreDisposable?: IDisposable;
    private signinSilentPromise?: Promise<User>;
    private readonly httpClient: HttpClient;
    private readonly authority: string;
    private readonly userManager: UserManager;
}

export const defaultTableColumns = [
    { option: 'Row #' },
    { option: 'Serial Number' },
    //{ option: 'Unit Serial Number' },
    //{ option: 'Display ID' },
    //{ option: 'Site ID' },
    //{ option: 'Address' },
    //{ option: 'IP Address' },
    //{ option: 'New Events' },
    //{ option: 'Display Alerts' },
    //{ option: 'Tickets' },
    //{ option: 'Last Poll Time' },
    //{ option: 'Status' },
];

export const defaultManifestColumns = [
    { option: 'Serial Number' },
    { option: 'Unit Serial Number' },
    { option: 'Mri Software' },
    { option: 'Last Poll Time' },
    { option: 'Status' },
];

export const defaultAggregateColumns = [
    { option: 'Serial Number' },
    { option: 'Display ID' },
    { option: 'Site ID' },
    { option: 'Address' },
    { option: 'City' },
    { option: 'State Or Province' },
    { option: 'Runtime Percent' },
    { option: 'Play Count' },
    { option: 'Play Duration Average' },
];

export const NO_ACTIVE_PRESET = 'None';

class AccountStorage {
    public static version = 12;

    public isValid() {
        return this.version == AccountStorage.version;
    }

    @observable
    public version = AccountStorage.version;
    @observable
    public viewType = DisplayViewTypes.TableLarge;
    @observable
    public wrappedHeaders: boolean = false;
    @observable
    public hideUnits: boolean = false;
    @observable
    public mapZoomLevel: number = 12;
    @observable
    public mapShowDisplayList: boolean = true;
    @observable
    public presetColumns: IUserColumnPreset[] = [];
    @observable
    public presetStickyColumns: IUserColumnPreset[] = [];
    @observable
    public activeColumnPreset: string = '';
    @observable
    public activeStickyPreset: string = '';
    @observable
    public displayTableStickyColumns: string[] = [];
    @observable
    public displayTableColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[] = defaultTableColumns.slice();
    @observable
    public displayTableConfigColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[] = [];
    @observable
    public displayAggregateColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[] =
        defaultAggregateColumns.slice();
    // GRID SETTINGS
    @observable
    public noActivePresetGridColumns: ColumnState[] = [];
    @observable
    public activeGridColumnPreset: string = '';
    @observable
    public gridAutoUpdatePresets: boolean = false;
    @observable
    public gridColumnPresets: IUserGridColumnPreset[] = [];
    @observable
    public activeGridFilter: FilterModel = {};
    @observable
    public activeGridFilterPreset: string = '';
    @observable
    public gridFilterPresets: IUserGridFilterPreset[] = [];
    @observable
    public gridThemeSize:'grid-xxsmall' |'grid-xsmall' | 'grid-small' | 'grid-normal' | 'grid-large' = 'grid-normal';
    @observable
    public gridTheme: 'ag-theme-balham' | 'ag-theme-balham-dark' = 'ag-theme-balham';
    @observable
    public gridRowHeight = 30;
    @observable
    public gridPopVuRowHeight = 200;
    @observable
    public gridGroupHeaderHeight = 30;
    @observable
    public gridColumnWidth = 60;
    @observable
    public gridColumnPadding = 2;
    @observable
    public gridWrapText: boolean = false;
    @observable
    public gridBoldText: boolean = false;
    @observable
    public gridDecimals = 1;
    @observable
    public displaysPreference: 'list' | 'grid' = 'grid';
    @observable
    public gridCenterAlignColumns: boolean = false;
    @observable
    public gridCenterAlignHeaders: boolean = false;
    @observable
    public gridLeftPinnedContainerScrollable: boolean = false;
    @observable
    public gridLeftPinnedContainerWidth: number = 0.3;
    @observable
    public gridRightPinnedContainerScrollable: boolean = false;
    @observable
    public gridRightPinnedContainerWidth: number = 0.3;
}

class UserStorage {
    public static version = 1;

    public isValid() {
        return this.version == UserStorage.version;
    }

    @observable
    public version = UserStorage.version;
    @observable
    public user?: Mri.Web.Internal.Controllers.Account.UserModel;
}

class TabStorage {
    public static version = 1;

    public isValid() {
        return this.version == TabStorage.version;
    }

    @observable
    public version = TabStorage.version;
    @observable
    public activeGridColumns: ColumnState[] = [];
    @observable
    public activeGridColumnPreset: string = '';
    @observable
    public gridLeftScrollPosition: number = 0;
    @observable
    public gridRightScrollPosition: number = 0;
    @observable
    public gridCenterScrollPosition: number = 0;
}
